'use client';

import { useState } from 'react';
import { useI18n } from '@fshr/context/i18n-context';
import { Button, ErrorAlert, Icon, LoadingIndicator } from '@fshr/components';
import { useRecaptcha } from '@fshr/hooks';
import { useProfile } from '@profiles/lib/context/profile-context';
import { getErrorCodeForTranslations, ERROR_CODES, defaultFetcher } from '@fshr/shared';
import useSWR from 'swr';
import { cn } from '@fshr/shared-react';
import { requestEmailOtp, requestPhoneOtp, requestReauthOtp } from '@fshr/shared-react/server';
import RequestOTP, { AdditionarRequestOtpClasses } from './request-otp';
import SubmitOTP from './submit-otp';
import { toast } from 'sonner';
import { useTealium } from '@fshr/context/tealium-context';

export const OTP_VIEWS = {
	REQUEST_OTP: 'REQUEST_OTP',
	SUBMIT_OTP: 'SUBMIT_OTP',
	INVALID_OTP: 'INVALID_OTP',
};

export const LOGIN_TYPES = {
	PHONE_NUMBER: 'PHONE',
	EMAIL_ADDRESS: 'EMAIL',
};

interface OTPFormProps {
	onSuccess?: () => void;
	onSendViaChange?: (sendVia: string) => void;
	lockModal?: () => void;
	unlockModal?: () => void;
	onViewChange?: (view: string) => void;
	additionalClasses?: string;
	loadingIndicatorClasses?: string;
	additionalRequestOtpClasses?: AdditionarRequestOtpClasses;
}

function OTPForm({
	onSuccess = () => {},
	onSendViaChange = () => {},
	lockModal = () => {},
	unlockModal = () => {},
	onViewChange = () => {},
	additionalClasses = '',
	loadingIndicatorClasses,
	additionalRequestOtpClasses = {},
}: OTPFormProps) {
	const { t, locale: fsLocale, localeLink } = useI18n();
	const { trackLink } = useTealium();
	const { profile, isPersistedAuthenticated, submitReauthOtp, submitEmailOtp, submitPhoneOtp } =
		useProfile();
	const { recaptchaElementId, recaptchaCallbackName, recaptchaExecute, recaptchaReset } =
		useRecaptcha({ fsLocale });

	const {
		data: pageData,
		isLoading,
		error,
	} = useSWR(localeLink(`${process.env.NEXT_PUBLIC_BFF_BASE_URL}/countries/`), defaultFetcher, {
		revalidateIfStale: false,
		revalidateOnFocus: false,
		revalidateOnReconnect: false,
	});

	const countries = pageData?.countries;

	const sendCodeViaOptions = [LOGIN_TYPES.EMAIL_ADDRESS, LOGIN_TYPES.PHONE_NUMBER];

	const [view, _setView] = useState(OTP_VIEWS.REQUEST_OTP);
	const setView = (newView: string) => {
		_setView(newView);
		onViewChange(newView);
	};
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [phoneErrorCode, setPhoneErrorCode] = useState<string | null>(null);
	const [emailErrorCode, setEmailErrorCode] = useState<string | null>(null);
	const [errorCode, _setErrorCode] = useState<string | null>(null);
	const setErrorCode = (errorCode: string | null) => {
		_setErrorCode(errorCode);
		if (sendCodeVia === LOGIN_TYPES.EMAIL_ADDRESS) {
			setEmailErrorCode(errorCode);
		} else {
			setPhoneErrorCode(errorCode);
		}
	};

	const initalValues = isPersistedAuthenticated
		? [
				{ sendCodeVia: sendCodeViaOptions[0], sendCodeTo: profile?.emails?.[0]?.email },
				{ sendCodeVia: sendCodeViaOptions[1], sendCodeTo: profile?.phones?.[0]?.number },
			].filter(({ sendCodeTo }) => Boolean(sendCodeTo))[0]
		: { sendCodeVia: sendCodeViaOptions[0], sendCodeTo: '' };

	const [sendCodeVia, _setSendCodeVia] = useState(
		initalValues?.sendCodeVia || sendCodeViaOptions[0]
	);
	const [sendCodeTo, setSendCodeTo] = useState(initalValues?.sendCodeTo || '');
	const [submittedSendCodeTo, setSubmittedSendCodeTo] = useState('');

	const setSendCodeVia = (value: (typeof sendCodeViaOptions)[number]) => {
		_setSendCodeVia(value);
		onSendViaChange?.(value);

		if (isPersistedAuthenticated && value === LOGIN_TYPES.EMAIL_ADDRESS) {
			setSendCodeTo(profile?.emails?.[0]?.email || '');
		}

		if (isPersistedAuthenticated && value === LOGIN_TYPES.PHONE_NUMBER) {
			setSendCodeTo(profile?.phones?.[0]?.number || '');
		}
	};

	const reset = () => {
		setView(OTP_VIEWS.REQUEST_OTP);
		setIsSubmitting(false);
		setErrorCode(null);
	};

	const handleRequestOTP = () => {
		lockModal();
		setIsSubmitting(true);
		setErrorCode(null);
		recaptchaExecute().then(gRecaptchaResponse => {
			setSubmittedSendCodeTo(sendCodeTo);
			const otpRequest = !isPersistedAuthenticated
				? sendCodeVia === LOGIN_TYPES.EMAIL_ADDRESS
					? requestEmailOtp(sendCodeTo, gRecaptchaResponse)
					: requestPhoneOtp(`+${sendCodeTo}`, gRecaptchaResponse)
				: requestReauthOtp(sendCodeVia, gRecaptchaResponse);

			return otpRequest
				.then(([error]) => error && Promise.reject(error))
				.then(() => {
					recaptchaReset();
					setView(OTP_VIEWS.SUBMIT_OTP);
					unlockModal();
					setIsSubmitting(false);
				})
				.catch(e => {
					setErrorCode(e.errorCode || ERROR_CODES.UNKNOWN_ERROR_CODE);
					recaptchaReset();
					unlockModal();
					setIsSubmitting(false);
				})
				.finally(() => {
					scrollTo({
						top: 0,
						behavior: 'smooth',
					});
				});
		});
	};

	const handleSubmitOTP = ({
		otp,
		persistLogin = false,
	}: {
		otp: string;
		persistLogin?: boolean;
	}) => {
		lockModal();
		setIsSubmitting(true);
		setErrorCode(null);
		const isReAuth = isPersistedAuthenticated;
		const otpSubmit = !isReAuth
			? sendCodeVia === LOGIN_TYPES.EMAIL_ADDRESS
				? submitEmailOtp({ email: sendCodeTo, otp, persistLogin })
				: submitPhoneOtp({ phone: `+${sendCodeTo}`, otp, persistLogin })
			: submitReauthOtp({ loginType: sendCodeVia, otp });

		return otpSubmit
			.then(newProfile => {
				unlockModal();
				isReAuth &&
					toast.success(t('You have signed in successfully.'), {
						action: (
							<button onClick={() => toast.dismiss()} aria-label={t('Close')}>
								<Icon icon="close" />
							</button>
						),
					});
				trackLink('login', {
					page_detail: 'enter_code',
					login_method: sendCodeVia.toLowerCase(),
					enable_persistent_login: isReAuth ? 'na' : persistLogin ? 'enabled' : 'disabled',
					login_type: isReAuth ? 're-authentication' : 'full login',
					golden_id: newProfile?.goldenId || 'na',
				});
				onSuccess();
			})
			.catch(e => {
				setErrorCode(e?.errorCode || ERROR_CODES.UNKNOWN_ERROR_CODE);
				onViewChange(OTP_VIEWS.INVALID_OTP);
				recaptchaReset();
				unlockModal();
				setIsSubmitting(false);
			});
	};

	const renderRecaptchaContainer = () => (
		<div
			id={recaptchaElementId}
			data-callback={recaptchaCallbackName}
			className="recaptcha-container"
		/>
	);

	if (!pageData && !isLoading) {
		if (error) {
			console.error('swr error: showed error', error);
		}
		return (
			<>
				<ErrorAlert />
				{/* recaptcha container so recaptcha hook doesn't thwow an error */}
				{renderRecaptchaContainer()}
			</>
		);
	}

	if (error) {
		console.error('swr error: showed cached result', error);
	}

	return (
		<>
			{(isSubmitting || isLoading) && (
				<LoadingIndicator
					className={loadingIndicatorClasses || 'fixed top-0 right-0 left-0 bottom-0 h-full'}
				/>
			)}
			<div
				className={cn({
					'flex h-full items-center px-100 w-full':
						errorCode &&
						[
							ERROR_CODES.INVALID_LOGIN_OTP_CODE,
							ERROR_CODES.INVALID_LOGIN_OTP_EXPIRED,
							ERROR_CODES.VERIFY_EMAIL_CODE_EXPIRED,
							ERROR_CODES.UNKNOWN_ERROR_CODE,
						].includes(errorCode),
				})}
			>
				{(() => {
					if (
						errorCode &&
						[
							ERROR_CODES.INVALID_LOGIN_OTP_CODE,
							ERROR_CODES.INVALID_LOGIN_OTP_EXPIRED,
							ERROR_CODES.VERIFY_EMAIL_CODE_EXPIRED,
							ERROR_CODES.UNKNOWN_ERROR_CODE,
						].includes(errorCode)
					) {
						return (
							<div className={`w-full flex flex-col items-center lg:pt-0 ${additionalClasses}`}>
								<h1 className="mb-200 text-h2 lg:uppercase">
									{[
										ERROR_CODES.INVALID_LOGIN_OTP_EXPIRED,
										ERROR_CODES.VERIFY_EMAIL_CODE_EXPIRED,
									].includes(errorCode)
										? t('Code Expired')
										: t('Code Invalid')}
								</h1>
								<p className="mb-300 text-body2 text-center">
									{t(getErrorCodeForTranslations(errorCode))}
								</p>
								<Button onClick={reset}>{t('Get new code')}</Button>
							</div>
						);
					}

					if (view === OTP_VIEWS.REQUEST_OTP) {
						return (
							<RequestOTP
								isUserPersistent={isPersistedAuthenticated}
								sendCodeViaOptions={sendCodeViaOptions}
								sendCodeVia={sendCodeVia}
								sendCodeTo={sendCodeTo}
								submittedSendCodeTo={submittedSendCodeTo}
								error={sendCodeVia === LOGIN_TYPES.EMAIL_ADDRESS ? emailErrorCode : phoneErrorCode}
								setSendCodeVia={setSendCodeVia}
								setSendCodeTo={setSendCodeTo}
								onSubmit={handleRequestOTP}
								countries={countries}
								additionalRequestOtpClasses={additionalRequestOtpClasses}
							/>
						);
					}

					if (view === OTP_VIEWS.SUBMIT_OTP) {
						return (
							<SubmitOTP
								isUserPersistent={isPersistedAuthenticated}
								sendCodeTo={sendCodeTo}
								messageType={sendCodeVia === LOGIN_TYPES.EMAIL_ADDRESS ? 'email' : 'SMS'}
								onSubmit={handleSubmitOTP}
								reset={reset}
							/>
						);
					}

					return null;
				})()}

				{renderRecaptchaContainer()}
			</div>
		</>
	);
}

// Will load any required data for the OTP form
export default function OTPFormContainer(props: OTPFormProps) {
	const { isInitializing } = useProfile();
	if (isInitializing) {
		return <LoadingIndicator />;
	}
	return <OTPForm {...props} />;
}
